"use client";

import { BrickWall } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { authService } from "@/lib/firebase/auth";
import { cn } from "@/lib/utils";
import CustomH1 from "./custom-h1";
import { Card } from "./ui/card";

export function LoginForm({
	className,
	...props
}: React.ComponentPropsWithoutRef<"form">) {
	const t = useTranslations("Auth");
	const [_email] = useState("");
	const [_password] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const router = useRouter();

	const _handleEmailLogin = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!_email || !_password) {
			toast.error(t("enterEmailAndPassword"));
			return;
		}

		setIsLoading(true);

		try {
			await authService.signIn(_email, _password);
			// User will be redirected by the AuthGuard or GuestGuard component
			router.push("/dashboard");
		} catch (error: unknown) {
			console.error("Login error:", error);
			toast.error(t("loginFailedTitle"), {
				description:
					error instanceof Error ? error.message : t("loginFailedDesc"),
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleLogin = async () => {
		setIsLoading(true);

		try {
			await authService.signInWithGoogle();
		} catch (error: unknown) {
			console.error("Google login error:", error);
			toast.error(t("googleLoginFailedTitle"), {
				description:
					error instanceof Error ? error.message : t("googleLoginFailedDesc"),
			});
			//TODO: Should cancel
			authService.signOut();
			router.push("/login");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="relative w-full max-w-md mx-auto">
			{/* Animated gradient background */}
			<div className="absolute inset-0 -z-20 overflow-hidden rounded-2xl">
				{/* Primary animated gradient */}
				<div
					className="absolute inset-0  from-[hsl(var(--landing-page-accent-hsl))] via-primary to-[hsl(var(--landing-page-accent-hsl))]/10 opacity-20 bg-[length:400%_400%]"
					style={{ animation: "gradient 15s linear infinite" }}
				></div>

				{/* Secondary rotating gradient */}
				<div className="absolute inset-0 bg-gradient-to-tr from-primary/30 via-transparent to-[hsl(var(--landing-page-accent-hsl))]/30 opacity-40 animate-pulse"></div>

				{/* Floating orbs */}
				<div
					className="absolute top-1/4 left-1/4 w-20 h-20 bg-gradient-to-r from-[hsl(var(--landing-page-accent-hsl))]/40 to-primary/40 rounded-full blur-xl"
					style={{ animation: "float 6s ease-in-out infinite" }}
				></div>
				<div
					className="absolute bottom-1/3 right-1/4 w-16 h-16 bg-gradient-to-l from-primary/30 to-[hsl(var(--landing-page-accent-hsl))]/30 rounded-full blur-lg"
					style={{ animation: "float-delayed 7s ease-in-out infinite 2s" }}
				></div>
				<div
					className="absolute top-1/2 right-1/6 w-12 h-12 bg-gradient-to-br from-[hsl(var(--landing-page-accent-hsl))]/35 to-primary/25 rounded-full blur-md"
					style={{ animation: "float-slow 8s ease-in-out infinite 1s" }}
				></div>
			</div>

			{/* Subtle geometric patterns */}
			<div className="absolute inset-0 -z-10 overflow-hidden rounded-2xl">
				{/* Moving grid pattern */}
				<div
					className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.05)_1px,transparent_1px)] bg-[size:20px_20px] opacity-30"
					style={{ animation: "slide 20s linear infinite" }}
				></div>

				{/* Shimmer effect */}
				<div
					className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full"
					style={{ animation: "shimmer 3s ease-in-out infinite" }}
				></div>
			</div>

			{/* Main card */}
			<Card className="relative backdrop-blur-sm bg-card/80 border-0 shadow-xl shadow-black/10 dark:shadow-black/30 overflow-hidden">
				{/* Glass morphism overlay */}
				<div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-lg pointer-events-none"></div>

				{/* Subtle inner glow */}
				<div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--landing-page-accent-hsl))]/5 via-transparent to-primary/5 opacity-0 hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
				<form className={cn("flex flex-col gap-8 p-8", className)} {...props}>
					{/* Logo and brand */}
					<div className="flex justify-center">
						<div className="relative group">
							<BrickWall
								className="h-24 w-24 text-primary group-hover:scale-105 transition-transform duration-300"
								strokeWidth={1}
							/>
						</div>
					</div>

					{/* Header text */}
					<div className="flex flex-col items-center gap-3 text-center">
						<CustomH1 text={t("signIn")} />
						<p className="text-sm text-muted-foreground/80 max-w-sm leading-relaxed">
							{t("signInSubtitle")}
						</p>
						{/* Decorative line */}
						<div className="w-16 h-px bg-gradient-to-r from-transparent via-[hsl(var(--landing-page-accent-hsl))]/50 to-transparent"></div>
					</div>

					{/* Google Sign In Button */}
					<div className="space-y-4">
						<Button
							onClick={handleGoogleLogin}
							className="group relative w-full h-14 bg-gradient-to-r from-[hsl(var(--landing-page-accent-hsl))]/90 to-[hsl(var(--landing-page-accent-hsl))]/90 hover:from-white hover:to-white/90  dark:hover:from-gray-800 dark:hover:to-gray-700 border border-border/60 hover:border-[hsl(var(--landing-page-accent-hsl))]/30 shadow-lg hover:shadow-xl hover:shadow-black/10 transition-all duration-300 overflow-hidden"
							variant="outline"
							disabled={isLoading}
						>
							{/* Background effect */}

							{/* Content */}
							<div className="relative flex items-center justify-center gap-3">
								{/* Google Logo */}
								<div className="flex items-center justify-center w-6 h-6 bg-white rounded-full p-1 shadow-md">
									<svg
										aria-hidden="true"
										width="28"
										height="28"
										viewBox="0 0 24 24"
										className="w-7 h-7"
									>
										<path
											fill="#4285F4"
											d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
										/>
										<path
											fill="#34A853"
											d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
										/>
										<path
											fill="#FBBC05"
											d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
										/>
										<path
											fill="#EA4335"
											d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
										/>
									</svg>
								</div>

								{/* Text */}
								<span className="text-base text-white dark:text-black group-hover:text-[hsl(var(--landing-page-accent-hsl))] transition-colors duration-300">
									{t("signInWithGoogle")}
								</span>

								{/* Loading spinner */}
								{isLoading && (
									<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
								)}
							</div>
						</Button>

						{/* Footer text */}
						<p className="text-xs text-center text-muted-foreground/60 px-4">
							By signing in, you agree to our terms of service and privacy
							policy
						</p>
					</div>
				</form>
			</Card>
		</div>
	);
}
